import { useEffect, useMemo } from "react";
import { Zap } from "lucide-react";
import { STORM_LEVELS } from "./StormLayer";

const makeBolt = (h) => {
  let x = 0;
  let y = 0;
  const pts = ["M0,0"];
  const steps = 7 + Math.floor(Math.random() * 4);
  for (let i = 0; i < steps; i++) {
    x += (Math.random() - 0.5) * 60;
    y += h / steps;
    pts.push(`L${x.toFixed(0)},${y.toFixed(0)}`);
  }
  return pts.join(" ");
};

const playSurge = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    if (ctx.state === "suspended") ctx.resume();
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(55, now);
    osc.frequency.exponentialRampToValueAtTime(540, now + 1.4);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(0.16, now + 1.2);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 2.1);
    osc.connect(g).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 2.2);
    const len = Math.floor(ctx.sampleRate * 0.45);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 2);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const ng = ctx.createGain();
    ng.gain.value = 0.28;
    src.connect(ng).connect(ctx.destination);
    src.start(now + 1.25);
    setTimeout(() => ctx.close().catch(() => {}), 3200);
  } catch {
    /* audio blocked, skip */
  }
};

export const StormCelebration = ({ event, onDone }) => {
  const bolts = useMemo(
    () =>
      Array.from({ length: 9 }, (_, i) => ({
        id: i,
        x: 5 + Math.random() * 90,
        delay: Math.random() * 1.5,
        path: makeBolt(320 + Math.random() * 260),
        branch: makeBolt(180),
        cyan: i % 3 === 0,
      })),
    []
  );

  useEffect(() => {
    playSurge();
    const t = setTimeout(onDone, 4000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const raging = event.to === 3;

  return (
    <div className="fixed inset-0 z-[90] pointer-events-none overflow-hidden" data-testid="storm-celebration">
      {bolts.map((b) => (
        <span key={b.id}>
          <span
            className="celebration-flash absolute inset-0"
            style={{
              animationDelay: `${b.delay}s`,
              background: `radial-gradient(ellipse at ${b.x}% 0%, ${b.cyan ? "rgba(0,255,209,0.5)" : "rgba(170,120,255,0.55)"}, transparent 60%)`,
            }}
          />
          <svg
            className="celebration-bolt absolute top-0"
            style={{ left: `${b.x}%`, animationDelay: `${b.delay}s` }}
            width="160"
            height="620"
            viewBox="-80 0 160 620"
            aria-hidden="true"
          >
            <path d={b.path} stroke={b.cyan ? "#00FFD1" : "#e9dcff"} strokeWidth="3.4" fill="none" strokeLinejoin="round" />
            <path d={b.branch} transform="translate(0,140) rotate(24)" stroke={b.cyan ? "#9945FF" : "#00FFD1"} strokeWidth="1.8" fill="none" />
          </svg>
        </span>
      ))}

      <div className="absolute inset-0 flex items-center justify-center px-4">
        <div
          data-testid="storm-celebration-banner"
          className={`celebration-banner flex items-center justify-center gap-3 sm:gap-5 text-center px-6 py-5 sm:px-10 sm:py-6 rounded-2xl border backdrop-blur-md ${
            raging ? "border-[#00FFD1]/70 shadow-[0_0_60px_rgba(0,255,209,0.5)]" : "border-[#9945FF]/70 shadow-[0_0_60px_rgba(153,69,255,0.5)]"
          }`}
          style={{ background: "rgba(5,4,9,0.82)" }}
        >
          <Zap size={28} className="celebration-zap shrink-0 text-[#00FFD1]" fill="#00FFD1" aria-hidden="true" />
          <p data-testid="storm-celebration-tier" className="font-bebas text-3xl sm:text-5xl tracking-[0.12em] leading-none whitespace-nowrap">
            <span className="text-white">THE STORM IS </span>
            <span className={raging ? "text-[#00FFD1] storm-raging-text" : "gradient-sol"}>{STORM_LEVELS[event.to]}</span>
          </p>
          <Zap size={28} className="celebration-zap shrink-0 text-[#00FFD1]" fill="#00FFD1" aria-hidden="true" />
        </div>
      </div>
    </div>
  );
};
